import { useLingui } from "@lingui/react";
import { useForm, useWatch } from "react-hook-form";
import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CompanySelect, OrganizationSelect } from "@/components/common/entity-select";
import { useUoms } from "@/hooks/useUomReference";
import { uomLabel } from "@/lib/materials/conversion-grid";
import { AppDialog } from "@/components/common/AppDialog";
import {
  YIELD_AMOUNT_REQUIRED,
  createPrepRecipeSchema,
  type CreatePrepRecipeFormData,
} from "./schemas";

interface CreatePrepRecipeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: CreatePrepRecipeFormData, companyId: number) => void;
  /** Concept the user has no choice about. Hides the concept select entirely. */
  companyId?: number;
  /**
   * Concept the select opens on while staying changeable. A user who can filter
   * across concepts has picked one on the page behind this dialog; that is a
   * starting point, not a lock, so it must not arrive as `companyId`.
   */
  defaultCompanyId?: number | null;
  organizationId?: number | null;
  canSelectOrganization?: boolean;
  isPending?: boolean;
}

type TimeUnit = "minutes" | "hours" | "days";

const TIME_UNIT_TO_MINUTES: Record<TimeUnit, number> = {
  minutes: 1,
  hours: 60,
  days: 60 * 24,
};

function toMinutes(value: number | undefined, unit: TimeUnit): number | undefined {
  if (value == null) return undefined;
  return value * TIME_UNIT_TO_MINUTES[unit];
}

export function CreatePrepRecipeDialog({
  open,
  onOpenChange,
  onSave,
  companyId,
  defaultCompanyId,
  organizationId,
  canSelectOrganization = false,
  isPending = false,
}: CreatePrepRecipeDialogProps) {
  const { i18n } = useLingui();
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number | null>(
    organizationId ?? null,
  );
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    companyId ?? defaultCompanyId ?? null,
  );
  const [prepTimeUnit, setPrepTimeUnit] = useState<TimeUnit>("minutes");
  const [cookTimeUnit, setCookTimeUnit] = useState<TimeUnit>("minutes");
  const [contextError, setContextError] = useState<string | null>(null);
  // A batch is measured in any unit a recipe line can use, so another recipe
  // can take a share of it (UoM reference table, recipe = true).
  const { data: recipeUoms } = useUoms({ recipe: true }, open);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm<CreatePrepRecipeFormData>({
    resolver: zodResolver(createPrepRecipeSchema),
    defaultValues: {
      name: "",
      servings: undefined,
      prepTimeMinutes: undefined,
      cookTimeMinutes: undefined,
      totalYield: undefined,
      yieldUnit: "",
    },
  });
  const yieldUnit = useWatch({ control, name: "yieldUnit" }) ?? "";

  const onSubmit = (data: CreatePrepRecipeFormData) => {
    const resolvedCompanyId = companyId ?? selectedCompanyId;
    if (!resolvedCompanyId) {
      setContextError(i18n._("Select a concept before creating a prep recipe."));
      return;
    }

    onSave(
      {
        ...data,
        prepTimeMinutes: toMinutes(data.prepTimeMinutes, prepTimeUnit),
        cookTimeMinutes: toMinutes(data.cookTimeMinutes, cookTimeUnit),
        yieldUnit: data.yieldUnit || undefined,
      },
      resolvedCompanyId,
    );
    setContextError(null);
    reset();
  };

  return (
    <AppDialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        if (!v) {
          setContextError(null);
          setPrepTimeUnit("minutes");
          setCookTimeUnit("minutes");
          setSelectedOrganizationId(organizationId ?? null);
          setSelectedCompanyId(companyId ?? defaultCompanyId ?? null);
        }
        onOpenChange(v);
      }}
      title={i18n._("Create prep recipe")}
      description={i18n._(
        "Create a make-ahead recipe to use inside menu items.",
      )}
      footer={
        <div className="flex w-full justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {i18n._("Cancel")}
          </Button>
          <Button onClick={handleSubmit(onSubmit)} disabled={isPending}>
            {i18n._("Save & Proceed")}
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-2 gap-4">
        {!companyId && (
          <>
            {canSelectOrganization && (
              <div className="space-y-2">
                <Label>{i18n._("Organization")}</Label>
                <OrganizationSelect
                  value={selectedOrganizationId}
                  adminScope
                  onChange={(value) => {
                    setSelectedOrganizationId(value);
                    setSelectedCompanyId(null);
                    setContextError(null);
                  }}
                  onClear={() => {
                    setSelectedOrganizationId(null);
                    setSelectedCompanyId(null);
                  }}
                  placeholder={i18n._("Select organization")}
                />
              </div>
            )}

            {!companyId && (
              <div className="space-y-2">
                <Label>{i18n._("Concept")}</Label>
                <CompanySelect
                  value={selectedCompanyId}
                  organizationId={selectedOrganizationId ?? organizationId ?? undefined}
                  disabled={canSelectOrganization && !selectedOrganizationId}
                  onChange={(value) => {
                    setSelectedCompanyId(value);
                    setContextError(null);
                  }}
                  onClear={() => {
                    setSelectedCompanyId(null);
                  }}
                  placeholder={i18n._("Select concept")}
                />
              </div>
            )}
          </>
        )}

        <div className="space-y-2">
          <Label htmlFor="pr-name">{i18n._("Recipe name")}</Label>
          <Input
            id="pr-name"
            placeholder={i18n._("Enter recipe name...")}
            {...register("name")}
          />
          {errors.name && (
            <p className="text-xs text-(--color-danger-text)">{errors.name.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="pr-servings">{i18n._("Servings")}</Label>
          <Input
            id="pr-servings"
            type="number"
            min={1}
            step={1}
            placeholder={i18n._("Enter")}
            {...register("servings", {
              setValueAs: (v: string) => (v === "" ? undefined : Number(v)),
            })}
          />
          {errors.servings && (
            <p className="text-xs text-(--color-danger-text)">{errors.servings.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="pr-yield">{i18n._("Batch yield")}</Label>
          <Input
            id="pr-yield"
            type="number"
            min={0}
            step="any"
            placeholder={i18n._("E.g. 1.2")}
            {...register("totalYield", {
              setValueAs: (v: string) => (v === "" ? undefined : Number(v)),
            })}
          />
          {errors.totalYield && (
            <p className="text-xs text-(--color-danger-text)">
              {errors.totalYield.message === YIELD_AMOUNT_REQUIRED
                ? i18n._("Enter how much one batch makes")
                : errors.totalYield.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="pr-yield-unit">{i18n._("Yield unit")}</Label>
          <Select value={yieldUnit} onValueChange={(v) => setValue("yieldUnit", v, { shouldValidate: !!errors.yieldUnit })}>
            <SelectTrigger id="pr-yield-unit" aria-label={i18n._("Yield unit")}>
              <SelectValue placeholder={i18n._("Select unit")} />
            </SelectTrigger>
            <SelectContent>
              {(recipeUoms ?? []).map((uom) => (
                <SelectItem key={uom.code} value={uom.code}>
                  {uomLabel(uom)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.yieldUnit ? (
            <p className="text-xs text-(--color-danger-text)">{i18n._("Choose the unit the batch is measured in")}</p>
          ) : (
            <p className="text-xs text-(--color-text-secondary)">
              {i18n._("Costed per yield unit, so other recipes pay for the share they use.")}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="pr-prep">{i18n._("Prep time")}</Label>
          <div className="flex gap-2">
            <Input
              id="pr-prep"
              type="number"
              placeholder={i18n._("Enter")}
              className="flex-1"
              {...register("prepTimeMinutes", {
                setValueAs: (v: string) => (v === "" ? undefined : Number(v)),
              })}
            />
            <Select value={prepTimeUnit} onValueChange={(value) => setPrepTimeUnit(value as TimeUnit)}>
              <SelectTrigger className="w-[9rem]" aria-label={i18n._("Prep time unit")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="minutes">{i18n._("Minutes")}</SelectItem>
                <SelectItem value="hours">{i18n._("Hours")}</SelectItem>
                <SelectItem value="days">{i18n._("Days")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {errors.prepTimeMinutes && (
            <p className="text-xs text-(--color-danger-text)">{errors.prepTimeMinutes.message}</p>
          )}
          {!errors.prepTimeMinutes && (
            <p className="text-xs text-(--color-text-secondary)">
              {i18n._("Saved as minutes")}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="pr-cook">{i18n._("Cooking time")}</Label>
          <div className="flex gap-2">
            <Input
              id="pr-cook"
              type="number"
              placeholder={i18n._("Enter")}
              className="flex-1"
              {...register("cookTimeMinutes", {
                setValueAs: (v: string) => (v === "" ? undefined : Number(v)),
              })}
            />
            <Select value={cookTimeUnit} onValueChange={(value) => setCookTimeUnit(value as TimeUnit)}>
              <SelectTrigger className="w-[9rem]" aria-label={i18n._("Cooking time unit")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="minutes">{i18n._("Minutes")}</SelectItem>
                <SelectItem value="hours">{i18n._("Hours")}</SelectItem>
                <SelectItem value="days">{i18n._("Days")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {errors.cookTimeMinutes && (
            <p className="text-xs text-(--color-danger-text)">{errors.cookTimeMinutes.message}</p>
          )}
          {!errors.cookTimeMinutes && (
            <p className="text-xs text-(--color-text-secondary)">
              {i18n._("Saved as minutes")}
            </p>
          )}
        </div>

        {contextError && (
          <p className="col-span-2 text-xs text-(--color-danger-text)">{contextError}</p>
        )}
      </div>
    </AppDialog>
  );
}
